import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Switch, Tooltip, Title } from "@bsf/force-ui";
import { __, sprintf } from '@wordpress/i18n';
import toast, { Toaster } from 'react-hot-toast';
import apiFetch from '@wordpress/api-fetch';
import {
	Copy, Check, ExternalLink, Sparkles,
	RotateCw, ChevronRight, Search,
	Sun, FilePenLine, Server, Clock, Lock,
	AlertTriangle,
} from 'lucide-react';

// WP MCP Adapter feature plugin — required for the dedicated MCP endpoint.
const MCP_ADAPTER_GITHUB = 'https://github.com/WordPress/mcp-adapter';
const MCP_ADAPTER_DOWNLOAD = 'https://github.com/WordPress/mcp-adapter/releases/latest/download/mcp-adapter.zip';

// Brand purple is needed as a JS value for the react-hot-toast icon theme
// (a component prop, not a stylable element). All visual styling lives in
// src/styles.css under .hfe-ai-tools.
const BRAND = '#6005ff';

// ───────── Reusable bits ─────────

const Card = ({ children }) => <div className="hfe-ai-card">{children}</div>;

const CardHead = ({ title, description, right }) => (
	<div className="hfe-ai-cardhead">
		<div className="hfe-ai-cardhead-main">
			<h3 className="hfe-ai-c-ink text-base font-semibold m-0">{title}</h3>
			{description && <p className="hfe-ai-c-muted text-sm font-normal m-0">{description}</p>}
		</div>
		{right}
	</div>
);

const HeaderBadge = ({ tone = 'ok', dot, dotClass = '', children }) => (
	<span className={`hfe-ai-hbadge text-xs font-semibold ${tone === 'ok' ? 'is-ok' : ''}`}>
		{dot && <span className={`hfe-ai-hbadge-dot ${dotClass}`} />}
		{children}
	</span>
);

const Chip = ({ tone = 'ok', children }) => (
	<span className={`hfe-ai-chip text-xxs font-semibold ${tone === 'ok' ? 'is-ok' : 'is-muted'}`}>
		<span className="hfe-ai-chip-dot" />
		{children}
	</span>
);

// ───────── Main component ─────────

const AITools = () => {
	// Guard the localized global so a missing/blocked localize script degrades
	// gracefully instead of throwing a ReferenceError and white-screening.
	const hfeSettingsData = window.hfeSettingsData || {};
	const initialSettings = hfeSettingsData.mcp_settings || {};
	const isAbilitiesApiActive = hfeSettingsData.is_abilities_api_active;
	const isMcpAdapterActive = hfeSettingsData.is_mcp_adapter_active;
	const isAngieActive = hfeSettingsData.is_angie_active;
	const siteUrl = hfeSettingsData.site_url || window.location.origin;
	const currentUser = hfeSettingsData.current_user || 'admin';
	const restUrl = hfeSettingsData.rest_url || (siteUrl.replace(/\/$/, '') + '/wp-json/');
	const appPasswordUrl = hfeSettingsData.application_passwords_url || (siteUrl + '/wp-admin/profile.php#application-passwords-section');

	const [settings, setSettings] = useState({
		enable_abilities: initialSettings.enable_abilities ?? false,
		allow_modifications: initialSettings.allow_modifications ?? true,
		dedicated_server: initialSettings.dedicated_server ?? false,
		angie_enabled: initialSettings.angie_enabled ?? false,
		disabled_abilities: initialSettings.disabled_abilities ?? [],
	});

	// Mirror of `settings` that is always current within the same tick. Toggle
	// handlers read/update this ref instead of the (possibly stale) `settings`
	// closure, so flipping two switches in quick succession accumulates correctly
	// instead of one overwriting the other.
	const settingsRef = useRef(settings);
	useEffect(() => { settingsRef.current = settings; }, [settings]);

	const [saving, setSaving] = useState(false);
	const [abilities, setAbilities] = useState([]);
	const [expandedGroups, setExpandedGroups] = useState({});
	const [pageTab, setPageTab] = useState('overview');
	const [scopeTab, setScopeTab] = useState('uae-only');
	const [copied, setCopied] = useState(false);
	const [promptCopied, setPromptCopied] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [filterMode, setFilterMode] = useState('all');

	useEffect(() => {
		apiFetch({
			path: '/hfe/v1/mcp-abilities',
			headers: { 'X-WP-Nonce': hfeSettingsData.hfe_nonce_action },
		}).then((data) => {
			if (Array.isArray(data)) setAbilities(data);
		}).catch(() => {});
	}, []);

	// Bounce off Server & Connect tab if its pre-conditions go away.
	useEffect(() => {
		if (pageTab === 'server' && (!settings.enable_abilities || !settings.dedicated_server)) {
			setPageTab('overview');
		}
	}, [pageTab, settings.enable_abilities, settings.dedicated_server]);

	const namespaceLabels = {
		'hfe-info': __('Plugin Info', 'header-footer-elementor'),
		'hfe-active-templates': __('Active Templates', 'header-footer-elementor'),
		'hfe-templates': __('Templates & Builder', 'header-footer-elementor'),
		'hfe-pages': __('Pages', 'header-footer-elementor'),
		'hfe-widgets': __('Widgets', 'header-footer-elementor'),
		'hfe-extensions': __('Extensions', 'header-footer-elementor'),
		'hfe-display-rules': __('Display Rules', 'header-footer-elementor'),
		'hfe-theme-compat': __('Theme', 'header-footer-elementor'),
		'hfe-settings': __('Settings', 'header-footer-elementor'),
		'hfe-shortcodes': __('Shortcode', 'header-footer-elementor'),
		'hfe-design': __('Design System', 'header-footer-elementor'),
		'hfe-maintenance': __('Maintenance', 'header-footer-elementor'),
		'hfe-pro': __('Pro Features', 'header-footer-elementor'),
		'uael-info': __('Pro Plugin Info', 'header-footer-elementor'),
		'uael-skins': __('Post Skins', 'header-footer-elementor'),
		'uael-settings': __('Integrations & Branding', 'header-footer-elementor'),
	};

	const namespaceDescriptions = {
		'hfe-info': __('Plugin version, health, and compatibility info', 'header-footer-elementor'),
		'hfe-active-templates': __('See which header/footer is active on any page', 'header-footer-elementor'),
		'hfe-templates': __('Create, edit, and manage templates and page layouts', 'header-footer-elementor'),
		'hfe-pages': __('Create and manage WordPress pages', 'header-footer-elementor'),
		'hfe-widgets': __('Activate, deactivate, and list available widgets', 'header-footer-elementor'),
		'hfe-extensions': __('Toggle Scroll to Top and Reading Progress Bar', 'header-footer-elementor'),
		'hfe-display-rules': __('Control where templates appear on your site', 'header-footer-elementor'),
		'hfe-theme-compat': __('Theme detection and compatibility settings', 'header-footer-elementor'),
		'hfe-settings': __('Read and update plugin configuration', 'header-footer-elementor'),
		'hfe-shortcodes': __('Render template shortcodes', 'header-footer-elementor'),
		'hfe-design': __('Get site colors, fonts, and spacing', 'header-footer-elementor'),
		'hfe-maintenance': __('Clear caches and run maintenance tasks', 'header-footer-elementor'),
		'hfe-pro': __('UAE Pro upgrade information and pricing', 'header-footer-elementor'),
	};

	const groupedAbilities = useMemo(() => {
		const groups = {};
		abilities.forEach((ability) => {
			const cat = ability.category || 'other';
			if (!groups[cat]) groups[cat] = [];
			groups[cat].push(ability);
		});
		return groups;
	}, [abilities]);

	const readCount = useMemo(() => abilities.filter(a => a.readonly).length, [abilities]);
	const writeCount = abilities.length - readCount;

	const isAbilityEnabled = (a) => {
		if (!settings.enable_abilities) return false;
		if (!a.readonly && !settings.allow_modifications) return false;
		return !settings.disabled_abilities.includes(a.name);
	};

	const enabledCount = useMemo(
		() => abilities.filter(isAbilityEnabled).length,
		[abilities, settings.enable_abilities, settings.allow_modifications, settings.disabled_abilities]
	);

	const isGroupOn = (ns) => (groupedAbilities[ns] || []).every(a => !settings.disabled_abilities.includes(a.name));

	const groupEnabledCount = (list) => list.filter(a => !settings.disabled_abilities.includes(a.name)).length;

	// ── Save ──
	// Serialize saves: chain each POST after the previous one settles so rapid
	// toggles commit in the order the user made them, not the order the network
	// happens to complete them (which could let a stale write land last).
	const saveChainRef = useRef(Promise.resolve());

	// Last state the server actually confirmed. The optimistic UI is reverted to
	// this when a save fails, so the toggles never drift from what is persisted.
	const lastSavedRef = useRef(settings);

	// Monotonic id for each queued save. A failed save only rolls the UI back if
	// no newer change was queued after it; otherwise that pending save reconciles.
	const saveSeqRef = useRef(0);

	const doSave = async (data, seq) => {
		setSaving(true);
		try {
			const res = await apiFetch({
				path: '/hfe/v1/mcp-settings',
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': hfeSettingsData.hfe_nonce_action },
				body: JSON.stringify(data),
			});
			if (!res || !res.success) {
				throw new Error(res && res.message ? res.message : 'save_failed');
			}
			lastSavedRef.current = data;
			toast.success(__('Settings saved.', 'header-footer-elementor'));
		} catch (e) {
			// Surface the failure and undo the optimistic change so the UI matches
			// the server. Skip the revert if a newer save is already queued — it
			// carries the cumulative state and will land the correct values.
			if (seq === saveSeqRef.current) {
				// Clone so the reference differs from the current state object,
				// otherwise React bails out of the re-render and the failed
				// toggle would not visually revert.
				const restored = { ...lastSavedRef.current };
				settingsRef.current = restored;
				setSettings(restored);
			}
			toast.error(__('Failed to save settings.', 'header-footer-elementor'));
		} finally {
			setSaving(false);
		}
	};

	const saveSettings = (data) => {
		const seq = ++saveSeqRef.current;
		saveChainRef.current = saveChainRef.current.then(
			() => doSave(data, seq),
			() => doSave(data, seq)
		);
		return saveChainRef.current;
	};

	const handleToggle = (key, value) => {
		const updated = { ...settingsRef.current, [key]: value };
		settingsRef.current = updated;
		setSettings(updated);
		saveSettings(updated);
	};

	const handleGroupToggle = (ns, enabled) => {
		const groupNames = (groupedAbilities[ns] || []).map(a => a.name);
		let disabled = [...settingsRef.current.disabled_abilities];
		if (enabled) {
			disabled = disabled.filter(n => !groupNames.includes(n));
		} else {
			groupNames.forEach(n => { if (!disabled.includes(n)) disabled.push(n); });
		}
		const updated = { ...settingsRef.current, disabled_abilities: disabled };
		settingsRef.current = updated;
		setSettings(updated);
		saveSettings(updated);
	};

	const handleAbilityToggle = (name, enabled) => {
		let disabled = [...settingsRef.current.disabled_abilities];
		if (enabled) disabled = disabled.filter(n => n !== name);
		else if (!disabled.includes(name)) disabled.push(name);
		const updated = { ...settingsRef.current, disabled_abilities: disabled };
		settingsRef.current = updated;
		setSettings(updated);
		saveSettings(updated);
	};

	const handleResetDefaults = () => {
		const defaults = {
			enable_abilities: false,
			allow_modifications: true,
			dedicated_server: false,
			angie_enabled: false,
			disabled_abilities: [],
		};
		settingsRef.current = defaults;
		setSettings(defaults);
		saveSettings(defaults);
		setPageTab('overview');
	};

	// ── Connect snippet ──
	const siteHostname = useMemo(() => {
		try { return new URL(restUrl).hostname.replace(/[^a-zA-Z0-9-]/g, '-'); }
		catch (e) { return 'site'; }
	}, [restUrl]);

	const isUaeOnly = scopeTab === 'uae-only';
	const serverName = isUaeOnly ? `${siteHostname}-uae` : `${siteHostname}-global`;
	const serverUrl = isUaeOnly ? `${restUrl}uae/mcp` : `${restUrl}mcp/mcp-adapter-default-server`;
	const mcpServerEntry = {
		command: 'npx',
		args: ['-y', '@automattic/mcp-wordpress-remote@latest'],
		env: { WP_API_URL: serverUrl, WP_API_USERNAME: currentUser, WP_API_PASSWORD: 'your-application-password' },
	};
	const mcpConfig = JSON.stringify({ mcpServers: { [serverName]: mcpServerEntry } }, null, 2);
	const serverEntryJson = JSON.stringify({ [serverName]: mcpServerEntry }, null, 2);

	const aiPrompt = `Add this MCP server to my AI client so I can connect to my WordPress site:
${serverEntryJson}

Steps:
1. Ask which AI client I use if unsure (Claude Code, Claude Desktop, Cursor, Windsurf, VS Code, ChatGPT, etc.) and what OS I'm on (macOS, Windows, Linux) — both are needed to locate the correct config file.

2. Ask for my Application Password upfront (before proceeding to any other steps). The placeholder in the JSON above says "your-application-password" — replace it with what I provide. If I don't have one, direct me to:
   ${appPasswordUrl}

3. If using ChatGPT:
   Go to Settings → Connectors → Add Connector and paste this URL as the Connector URL:
   ${serverUrl}

   Use HTTP Basic Auth with my WordPress username and an Application Password.

   If I don't have an Application Password, direct me to:
   ${appPasswordUrl}

   See also:
   https://developers.openai.com/apps-sdk/deploy/connect-chatgpt

   Skip steps 4–8.

4. Locate the correct MCP config file for my client and OS. Verify the path exists on my system — do not guess. If it doesn't exist, confirm the path with me before creating.

5. Merge into existing config if present — do NOT overwrite other MCP server entries. For VS Code, use "servers" key instead of "mcpServers".

6. Detect my Node.js setup by running:
   which npx && node --version && echo $PATH

   - If you can run terminal commands directly (e.g. Claude Code, Cursor, Windsurf, VS Code agent), run this yourself.
   - If you are sandboxed and cannot run terminal commands (e.g. Claude Desktop, ChatGPT), ask me to run it and paste the output back.
   - Requires Node.js v20 or v22. If below v20 or v23+, warn me and stop.
   - Set "command" to the full npx path from "which npx" — never use a path from your own environment.
   - Add "PATH" to "env" using the actual $PATH output — include the node bin directory, Homebrew paths (/opt/homebrew/bin, /usr/local/bin), and system paths.
   - MCP servers don't inherit the user's shell PATH, so include everything needed for node, npx, and git.

7. If you're sandboxed (e.g. Claude Desktop) and can't write to the real filesystem, don't write to a sandbox path. Instead, output the final JSON in a code block with the file path so I can save it manually.

8. Verify the final JSON is valid and confirm what was added.`;

	const handleCopyConfig = () => {
		navigator.clipboard.writeText(mcpConfig).then(() => {
			setCopied(true);
			toast.success(__('Copied to clipboard', 'header-footer-elementor'));
			setTimeout(() => setCopied(false), 2000);
		});
	};
	const handleCopyEndpoint = () => {
		navigator.clipboard.writeText(serverUrl).then(() => {
			toast.success(__('Endpoint copied to clipboard', 'header-footer-elementor'));
		});
	};
	const handleCopyPrompt = () => {
		navigator.clipboard.writeText(aiPrompt).then(() => {
			setPromptCopied(true);
			toast.success(__('Prompt copied! Paste it into your AI client.', 'header-footer-elementor'));
			setTimeout(() => setPromptCopied(false), 2000);
		});
	};

	// ── Ability search/filter ──
	const matchesFilter = (a) => {
		if (filterMode === 'read' && !a.readonly) return false;
		if (filterMode === 'write' && a.readonly) return false;
		if (searchQuery) {
			const q = searchQuery.toLowerCase();
			return (a.label || '').toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q) || a.name.toLowerCase().includes(q);
		}
		return true;
	};

	const expandAll = () => {
		const allKeys = Object.keys(groupedAbilities);
		const allOpen = allKeys.every(k => expandedGroups[k]);
		setExpandedGroups(allOpen ? {} : Object.fromEntries(allKeys.map(k => [k, true])));
	};

	// ── Hard empty state — the Abilities API itself is unavailable ──
	if (!isAbilitiesApiActive) {
		return (
			<div className="hfe-ai-tools hfe-ai-empty">
				<Title description="" icon={null} iconPosition="right" size="sm" tag="h2" title={__('AI Tools', 'header-footer-elementor')} />
				<div className="hfe-ai-empty-note text-sm">
					<strong className="hfe-ai-c-ink">{__('Requires WordPress 6.8+', 'header-footer-elementor')}</strong>{' '}
					{__('AI Tools requires WordPress 6.8 or later, which includes the Abilities API.', 'header-footer-elementor')}
				</div>
				<Toaster position="top-right" />
			</div>
		);
	}

	const enabled = settings.enable_abilities;
	const mcpOn = enabled && settings.dedicated_server;
	const editOn = enabled && settings.allow_modifications;
	const angieOn = enabled && settings.angie_enabled && isAngieActive;

	const progressPct = abilities.length ? Math.round((enabledCount / abilities.length) * 100) : 0;

	// ── Render ──
	return (
		<div className="hfe-ai-tools">

			{/* Page header */}
			<div className="hfe-ai-header">
				<div>
					<Title description="" icon={null} iconPosition="right" size="sm" tag="h2" title={__('AI Tools', 'header-footer-elementor')} />
					<div className="hfe-ai-badges">
						{enabled
							? <HeaderBadge tone="ok" dot>{__('Abilities enabled', 'header-footer-elementor')}</HeaderBadge>
							: <HeaderBadge tone="neutral" dot>{__('Abilities disabled', 'header-footer-elementor')}</HeaderBadge>}
						<HeaderBadge tone="neutral" dot dotClass={enabled ? 'is-blue' : 'is-grey'}>
							{sprintf(__('%1$d / %2$d abilities', 'header-footer-elementor'), enabled ? enabledCount : 0, abilities.length || 0)}
						</HeaderBadge>
					</div>
				</div>
				<button
					onClick={handleResetDefaults}
					disabled={false}
					className="hfe-ai-reset text-xs font-medium"
				>
					<RotateCw size={14} />
					{__('Reset to defaults', 'header-footer-elementor')}
				</button>
			</div>

			{/* Settings card with 4 toggles */}
			<Card>
				<CardHead
					title={__('Settings', 'header-footer-elementor')}
					description={__('Control how UAE registers AI tools with WordPress and exposes them to clients.', 'header-footer-elementor')}
				/>
				<div>
					{/* Master toggle */}
					<ToggleRow
						emphasized
						icon={<Sun size={18} />}
						title={__('Enable Abilities', 'header-footer-elementor')}
						description={__("Register UAE AI tools with WordPress so they can be used by MCP clients (such as Claude, Cursor, and VS Code) and Elementor's Angie assistant. Disabled by default — enable this option to expose AI tools.", 'header-footer-elementor')}
						value={settings.enable_abilities}
						onChange={(v) => handleToggle('enable_abilities', v)}
					/>

					<ToggleRow
						icon={<FilePenLine size={18} />}
						title={__('Enable Edit Abilities', 'header-footer-elementor')}
						tag="write"
						locked={!enabled}
						description={__('When enabled, AI clients can create, edit, and delete templates, pages, and widget settings. When disabled, AI clients have read-only access to your data.', 'header-footer-elementor')}
						value={settings.allow_modifications}
						onChange={(v) => handleToggle('allow_modifications', v)}
						disabled={!enabled}
					/>

					<ToggleRow
						icon={<Server size={18} />}
						title={__('Enable MCP Server', 'header-footer-elementor')}
						locked={!enabled}
						description={__('Creates a dedicated UAE MCP endpoint that AI clients like Claude, Cursor, and VS Code can connect to. When disabled, the endpoint is removed and external AI clients cannot discover or call any UAE abilities.', 'header-footer-elementor')}
						value={settings.dedicated_server}
						onChange={(v) => handleToggle('dedicated_server', v)}
						disabled={!enabled}
					/>

					<ToggleRow
						icon={<Clock size={18} />}
						title={__('Angie Integration', 'header-footer-elementor')}
						locked={!enabled || !isAngieActive}
						description={isAngieActive
							? __("Enable UAE abilities within Elementor's Angie AI assistant so it can use them in-browser without an external MCP client.", 'header-footer-elementor')
							: __('Install and activate the Angie plugin to use in-browser AI.', 'header-footer-elementor')}
						value={settings.angie_enabled && isAngieActive}
						onChange={(v) => handleToggle('angie_enabled', v)}
						disabled={!enabled || !isAngieActive}
					/>
				</div>
			</Card>

			{/* Tabs + panels — only when master is on */}
			{enabled && (
				<>
					<div className="hfe-ai-tabs">
						<TabButton active={pageTab === 'overview'} onClick={() => setPageTab('overview')}>
							{__('Overview', 'header-footer-elementor')}
						</TabButton>
						<TabButton active={pageTab === 'abilities'} onClick={() => setPageTab('abilities')}>
							{__('Abilities', 'header-footer-elementor')}
							<span className={`hfe-ai-tab-count text-xxs font-semibold ${pageTab === 'abilities' ? 'is-active' : ''}`}>
								{abilities.length}
							</span>
						</TabButton>
						{mcpOn && (
							<TabButton active={pageTab === 'server'} onClick={() => setPageTab('server')}>
								{__('Server & Connect', 'header-footer-elementor')}
							</TabButton>
						)}
					</div>

					{/* Overview */}
					{pageTab === 'overview' && (
						<Card>
							<CardHead title={__('Connection status', 'header-footer-elementor')} description={__('How AI clients reach your site right now', 'header-footer-elementor')} />

							{/* System status banner */}
							<div className={`hfe-ai-connbanner ${enabled ? 'is-active' : 'is-off'}`}>
								<span className="hfe-ai-connbanner-dot" />
								<div className="hfe-ai-connbanner-main">
									<div className="hfe-ai-connbanner-title text-sm font-semibold">
										{enabled
											? __('AI Tools are active', 'header-footer-elementor')
											: __('AI Tools are off', 'header-footer-elementor')}
									</div>
									<div className="hfe-ai-connbanner-sub text-xs">
										{enabled
											? sprintf(
												/* translators: 1: MCP server state, 2: access mode, 3: enabled count, 4: total */
												__('%1$s · %2$s · %3$d of %4$d abilities exposed', 'header-footer-elementor'),
												mcpOn ? __('MCP server on', 'header-footer-elementor') : __('MCP server off', 'header-footer-elementor'),
												editOn ? __('Read & write', 'header-footer-elementor') : __('Read-only', 'header-footer-elementor'),
												enabledCount,
												abilities.length
											)
											: __('Enable “Enable Abilities” above to connect AI clients.', 'header-footer-elementor')}
									</div>
								</div>
							</div>

							{/* Status cards */}
							<div className="hfe-ai-conngrid">
								{/* External MCP server */}
								<div className="hfe-ai-conncard">
									<div className="hfe-ai-conncard-head">
										<span className="hfe-ai-conncard-title text-sm font-semibold">{__('External MCP server', 'header-footer-elementor')}</span>
										<span className={`hfe-ai-pill ${mcpOn ? 'is-ok' : 'is-muted'}`}>{mcpOn ? __('Ready', 'header-footer-elementor') : __('Server off', 'header-footer-elementor')}</span>
									</div>
									{mcpOn ? (
										<>
											<div className="hfe-ai-conncard-endpoint">
												<span className="is-mono text-xs">{serverUrl}</span>
												<button onClick={handleCopyEndpoint} className="hfe-ai-iconbtn" aria-label={__('Copy endpoint', 'header-footer-elementor')}>
													<Copy size={13} />
												</button>
											</div>
											<button onClick={() => setPageTab('server')} className="hfe-ai-connlink text-xs font-medium">
												{__('Connect a client', 'header-footer-elementor')} <ChevronRight size={13} />
											</button>
										</>
									) : (
										<div className="hfe-ai-conncard-hint text-xs">{__('Turn on Enable MCP Server above to expose the endpoint.', 'header-footer-elementor')}</div>
									)}
								</div>

								{/* Angie (in-editor) */}
								<div className="hfe-ai-conncard">
									<div className="hfe-ai-conncard-head">
										<span className="hfe-ai-conncard-title text-sm font-semibold">{__('Angie (in-editor)', 'header-footer-elementor')}</span>
										<span className={`hfe-ai-pill ${angieOn ? 'is-ok' : 'is-muted'}`}>
											{isAngieActive ? (angieOn ? __('Enabled', 'header-footer-elementor') : __('Disabled', 'header-footer-elementor')) : __('Not installed', 'header-footer-elementor')}
										</span>
									</div>
									<div className="hfe-ai-conncard-hint text-xs">
										{isAngieActive
											? (angieOn
												? __('UAE tools are available in Elementor’s in-browser AI.', 'header-footer-elementor')
												: __('Turn on Angie Integration above to use it in the editor.', 'header-footer-elementor'))
											: __('Install the Angie plugin to use in-browser AI.', 'header-footer-elementor')}
									</div>
								</div>

								{/* Abilities exposed */}
								<div className="hfe-ai-conncard">
									<div className="hfe-ai-conncard-head">
										<span className="hfe-ai-conncard-title text-sm font-semibold">{__('Abilities exposed', 'header-footer-elementor')}</span>
										<span className="hfe-ai-conncard-count text-sm font-bold">{enabled ? enabledCount : 0}<span className="hfe-ai-c-muted text-xs font-medium"> / {abilities.length}</span></span>
									</div>
									<div className="hfe-ai-progress-track" style={{ marginBottom: '10px' }}>
										<div className="hfe-ai-progress-bar" style={{ width: `${enabled ? progressPct : 0}%` }} />
									</div>
									<button onClick={() => setPageTab('abilities')} className="hfe-ai-connlink text-xs font-medium">
										{__('Manage abilities', 'header-footer-elementor')} <ChevronRight size={13} />
									</button>
								</div>

								{/* Access mode */}
								<div className="hfe-ai-conncard">
									<div className="hfe-ai-conncard-head">
										<span className="hfe-ai-conncard-title text-sm font-semibold">{__('Access mode', 'header-footer-elementor')}</span>
										<span className={`hfe-ai-pill ${editOn ? 'is-brand' : 'is-muted'}`}>{editOn ? __('Read & write', 'header-footer-elementor') : __('Read-only', 'header-footer-elementor')}</span>
									</div>
									<div className="hfe-ai-conncard-hint text-xs">
										{editOn
											? __('AI can create, edit and delete. Toggle “Enable Edit Abilities” above for read-only.', 'header-footer-elementor')
											: __('AI can only read your data. Toggle “Enable Edit Abilities” above to allow changes.', 'header-footer-elementor')}
									</div>
								</div>
							</div>

							{/* Quick actions */}
							{enabled && (
								<div className="hfe-ai-connactions">
									<a href={appPasswordUrl} target="_blank" rel="noreferrer" className="hfe-ai-connbtn text-xs font-medium">
										<ExternalLink size={13} /> {__('Create application password', 'header-footer-elementor')}
									</a>
									{mcpOn && (
										<button onClick={() => setPageTab('server')} className="hfe-ai-connbtn text-xs font-medium">
											<Server size={13} /> {__('Server & connect', 'header-footer-elementor')}
										</button>
									)}
								</div>
							)}
						</Card>
					)}

					{/* Abilities */}
					{pageTab === 'abilities' && (
						<Card>
							<CardHead
								title={__('Ability toggles', 'header-footer-elementor')}
								description={__('Choose exactly which actions AI tools are allowed to perform', 'header-footer-elementor')}
								right={<Chip tone="ok">{sprintf(__('%1$d of %2$d enabled', 'header-footer-elementor'), enabledCount, abilities.length)}</Chip>}
							/>

							{/* Search + filters */}
							<div className="hfe-ai-toolbar">
								<div className="hfe-ai-search">
									<Search size={14} className="hfe-ai-c-muted" />
									<input
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										aria-label={__('Search abilities', 'header-footer-elementor')}
										placeholder={__('Search abilities - e.g. template, widget, page …', 'header-footer-elementor')}
										className="hfe-ai-search-input text-sm"
									/>
								</div>
								<div className="hfe-ai-filters">
									<FilterChip active={filterMode === 'all'} onClick={() => setFilterMode('all')} count={abilities.length}>{__('All', 'header-footer-elementor')}</FilterChip>
									<FilterChip active={filterMode === 'read'} onClick={() => setFilterMode('read')} count={readCount}>{__('Read', 'header-footer-elementor')}</FilterChip>
									<FilterChip active={filterMode === 'write'} onClick={() => setFilterMode('write')} count={writeCount}>{__('Write', 'header-footer-elementor')}</FilterChip>
								</div>
								<button
									onClick={expandAll}
									className="hfe-ai-expand text-sm font-medium"
								>
									{Object.keys(groupedAbilities).every(k => expandedGroups[k])
										? __('Collapse all', 'header-footer-elementor')
										: __('Expand all', 'header-footer-elementor')}
								</button>
							</div>

							{/* Groups */}
							{Object.entries(groupedAbilities).map(([ns, list]) => {
								const filtered = list.filter(matchesFilter);
								if (filtered.length === 0) return null;
								const isOpen = !!expandedGroups[ns];
								const onCount = groupEnabledCount(list);
								return (
									<div key={ns} className="hfe-ai-group">
										<div className="hfe-ai-group-head">
											<button
												type="button"
												className="hfe-ai-group-toggle"
												aria-expanded={isOpen}
												aria-controls={`hfe-ai-group-${ns}`}
												onClick={() => setExpandedGroups(p => ({ ...p, [ns]: !p[ns] }))}
											>
												<ChevronRight size={12} className={`hfe-ai-group-chevron ${isOpen ? 'is-open' : ''}`} />
												<div className="hfe-ai-group-letter text-xs font-bold">
													{(namespaceLabels[ns] || ns).charAt(0)}
												</div>
												<div className="hfe-ai-group-info">
													<div className="hfe-ai-c-ink text-sm font-semibold">
														{namespaceLabels[ns] || ns}
														<span className="hfe-ai-c-muted text-xs font-medium" style={{ marginLeft: 6 }}>
															{sprintf(__('%d abilities', 'header-footer-elementor'), list.length)}
														</span>
													</div>
													{namespaceDescriptions[ns] && (
														<div className="hfe-ai-c-muted text-xs">{namespaceDescriptions[ns]}</div>
													)}
												</div>
												<Chip tone={onCount === list.length ? 'ok' : 'muted'}>
													{onCount}/{list.length} {__('on', 'header-footer-elementor')}
												</Chip>
											</button>
											<Switch
												size="sm"
												value={isGroupOn(ns)}
												onChange={(v) => handleGroupToggle(ns, v)}
												disabled={false}
												aria-label={sprintf(__('Toggle all %s abilities', 'header-footer-elementor'), namespaceLabels[ns] || ns)}
											/>
										</div>
										{isOpen && (
											<div id={`hfe-ai-group-${ns}`}>
												{filtered.map((ability) => (
													<div key={ability.name} className="hfe-ai-arow">
														<div>
															<div className="hfe-ai-arow-title hfe-ai-c-ink text-sm font-semibold">
																{ability.label || ability.name}
																<TypeTag readonly={ability.readonly} />
															</div>
															<div className="hfe-ai-c-muted text-xs">{ability.description}</div>
														</div>
														<span />
														<Switch
															size="sm"
															value={isAbilityEnabled(ability)}
															onChange={(v) => handleAbilityToggle(ability.name, v)}
															disabled={!ability.readonly && !settings.allow_modifications}
															aria-label={sprintf(__('Toggle %s', 'header-footer-elementor'), ability.label || ability.name)}
														/>
													</div>
												))}
											</div>
										)}
									</div>
								);
							})}
						</Card>
					)}

					{/* Server & Connect — the WP MCP Adapter plugin powers the live endpoint.
					    The connect flow is always shown so users understand the setup; when the
					    adapter is missing we surface a notice above it. */}
					{pageTab === 'server' && mcpOn && (
						<>
							{!isMcpAdapterActive && <McpAdapterNotice />}
							<Card>
								<CardHead
									title={__('Connect your AI client', 'header-footer-elementor')}
									description={isMcpAdapterActive
										? __("UAE's MCP endpoint is live. Wire it into your AI tool below.", 'header-footer-elementor')
										: __('Once the WP MCP Adapter plugin is active, this is how you connect your AI client.', 'header-footer-elementor')}
									right={isMcpAdapterActive
										? <Chip tone="ok">{__('Endpoint live', 'header-footer-elementor')}</Chip>
										: <Chip tone="muted">{__('Setup required', 'header-footer-elementor')}</Chip>}
								/>
								<div className="hfe-ai-panel">
								<ol className="hfe-ai-steps text-sm">
									<li>
										{__('Create an Application Password', 'header-footer-elementor')}
										{' — '}
										<a href={appPasswordUrl} target="_blank" rel="noopener noreferrer" className="hfe-ai-link">
											{__('Open Application Passwords', 'header-footer-elementor')}
											<ExternalLink size={13} />
										</a>
									</li>
									<li>
										{__('Add to your AI client:', 'header-footer-elementor')}{' '}
										<a href="https://developer.wordpress.org/news/2026/02/from-abilities-to-ai-agents-introducing-the-wordpress-mcp-adapter/" target="_blank" rel="noopener noreferrer" className="hfe-ai-link">
											{__('Learn More', 'header-footer-elementor')}
											<ExternalLink size={13} />
										</a>
										<ul className="hfe-ai-substeps">
											<li><strong>{__('Claude Code', 'header-footer-elementor')}</strong>: <code className="hfe-ai-inlinecode text-xs">.mcp.json</code></li>
											<li><strong>{__('Claude Desktop', 'header-footer-elementor')}</strong>: <code className="hfe-ai-inlinecode text-xs">claude_desktop_config.json</code></li>
											<li><strong>{__('Cursor', 'header-footer-elementor')}</strong>: <code className="hfe-ai-inlinecode text-xs">.cursor/mcp.json</code></li>
											<li><strong>{__('VS Code', 'header-footer-elementor')}</strong>: <code className="hfe-ai-inlinecode text-xs">.vscode/mcp.json</code></li>
											<li><strong>{__('ChatGPT', 'header-footer-elementor')}</strong>: {__('go to Settings → Connectors → Add Connector, paste the endpoint URL, and use your WordPress username + Application Password.', 'header-footer-elementor')}</li>
										</ul>
									</li>
									<li>
										{__('Replace', 'header-footer-elementor')} <code className="hfe-ai-inlinecode text-xs">"your-application-password"</code> {__('in the JSON with the password from Step 1.', 'header-footer-elementor')}
									</li>
								</ol>

								{/* UAE Only / Global */}
								<div className="hfe-ai-scope">
									<ScopeBtn active={scopeTab === 'uae-only'} onClick={() => setScopeTab('uae-only')}>{__('UAE Only', 'header-footer-elementor')}</ScopeBtn>
									<ScopeBtn active={scopeTab === 'global'} onClick={() => setScopeTab('global')}>{__('Global', 'header-footer-elementor')}</ScopeBtn>
								</div>

								{/* Code block */}
								<div className="hfe-ai-code">
									<div className="hfe-ai-code-actions">
										<Tooltip content={__('Copy setup prompt for AI client', 'header-footer-elementor')} delay={50} arrow>
											<button onClick={handleCopyPrompt} className="hfe-ai-iconbtn" aria-label={__('Copy setup prompt', 'header-footer-elementor')}>
												{promptCopied ? <Check size={14} color="#95E1A0" /> : <Sparkles size={14} />}
											</button>
										</Tooltip>
										<Tooltip content={__('Copy JSON config', 'header-footer-elementor')} delay={50} arrow>
											<button onClick={handleCopyConfig} className="hfe-ai-iconbtn" aria-label={__('Copy JSON config', 'header-footer-elementor')}>
												{copied ? <Check size={14} color="#95E1A0" /> : <Copy size={14} />}
											</button>
										</Tooltip>
									</div>
									<pre className="hfe-ai-code-pre text-xs">
										{mcpConfig}
									</pre>
								</div>
							</div>
						</Card>
						</>
					)}
				</>
			)}

			<Toaster
				position="top-right" reverseOrder={false} gutter={8}
				containerStyle={{ top: 20, right: 20, marginTop: 80 }}
				toastOptions={{ duration: 5000, style: { background: 'white' }, success: { duration: 3000, iconTheme: { primary: BRAND, secondary: '#fff' } } }}
			/>
		</div>
	);
};

// ───────── Sub-components ─────────

const McpAdapterNotice = () => (
	<div className="hfe-ai-notice" role="status">
		<AlertTriangle size={20} className="hfe-ai-notice-icon" />
		<div className="hfe-ai-notice-main">
			<div className="hfe-ai-notice-title hfe-ai-c-ink text-base font-semibold">
				{__('MCP Adapter plugin not detected', 'header-footer-elementor')}
			</div>
			<p className="hfe-ai-notice-body hfe-ai-c-ink2 text-sm m-0">
				{__('To connect AI clients to your site, the', 'header-footer-elementor')}{' '}
				<a className="hfe-ai-notice-link" href={MCP_ADAPTER_GITHUB} target="_blank" rel="noopener noreferrer">
					{__('WP MCP Adapter', 'header-footer-elementor')}
				</a>{' '}
				{__('plugin is required. Download the latest release from GitHub, upload it via Plugins > Add New > Upload Plugin, and activate.', 'header-footer-elementor')}
			</p>
			<a className="hfe-ai-notice-btn text-sm font-semibold" href={MCP_ADAPTER_DOWNLOAD} target="_blank" rel="noopener noreferrer">
				{__('Download from GitHub', 'header-footer-elementor')}
			</a>
		</div>
	</div>
);

const ToggleRow = ({ emphasized, icon, title, description, tag, locked, value, onChange, disabled, switchSize = 'sm' }) => (
	<div className={`hfe-ai-trow ${emphasized ? 'is-emphasized' : ''} ${locked ? 'is-locked' : ''}`}>
		<div className="hfe-ai-trow-icon">
			{icon}
		</div>
		<div className="hfe-ai-trow-body">
			<div className={`hfe-ai-trow-title hfe-ai-c-ink font-semibold ${emphasized ? 'text-base' : 'text-sm'}`}>
				{title}
				{tag === 'write' && <span className="hfe-ai-tag is-write text-xxs font-semibold">{__('Write', 'header-footer-elementor')}</span>}
				{tag === 'read' && <span className="hfe-ai-tag is-read text-xxs font-semibold">{__('Read', 'header-footer-elementor')}</span>}
				{locked && <Lock size={12} className="hfe-ai-c-muted" />}
			</div>
			<div className="hfe-ai-trow-desc hfe-ai-c-muted text-sm">
				{description}
			</div>
		</div>
		<Switch size={switchSize} value={!!value} onChange={onChange} disabled={disabled} aria-label={typeof title === 'string' ? title : undefined} />
	</div>
);

const TabButton = ({ active, onClick, children }) => (
	<button
		onClick={onClick}
		className={`hfe-ai-tab text-sm ${active ? 'is-active font-semibold' : 'font-medium'}`}
	>
		{children}
	</button>
);

const FilterChip = ({ active, onClick, count, children }) => (
	<button
		onClick={onClick}
		className={`hfe-ai-fchip text-xs ${active ? 'is-active' : ''}`}
	>
		{children}
		<span className="hfe-ai-fchip-count text-xxs">{count}</span>
	</button>
);

const ScopeBtn = ({ active, onClick, children }) => (
	<button
		onClick={onClick}
		className={`hfe-ai-scope-btn text-sm ${active ? 'is-active font-semibold' : 'font-medium'}`}
	>
		{children}
	</button>
);

const TypeTag = ({ readonly }) => (
	<span className={`hfe-ai-tag text-xxs font-semibold ${readonly ? 'is-read' : 'is-write'}`}>
		{readonly ? __('Read', 'header-footer-elementor') : __('Write', 'header-footer-elementor')}
	</span>
);

export default AITools;
